import {GitCommitIcon} from '@primer/octicons-react'
import {Heading, type HeadingProps, Octicon, Timeline, type TimelineBadgeProps} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import type {PropsWithChildren} from 'react'

export type TimelineRowProps = {
  leadingVisual?: JSX.Element
  clipTimeline?: 'none' | 'bottom' | 'top'
  sx?: BetterSystemStyleObject
}

const itemSxMap = {
  none: {},
  bottom: {pb: 0},
  top: {pt: 0},
}

const DefaultLeadingVisual = () => {
  return <Octicon icon={GitCommitIcon} />
}

const defaultBadgeSx: TimelineBadgeProps['sx'] = {height: '16px', mt: 2}

function TimelineRowComponent({
  children,
  leadingVisual,
  clipTimeline = 'none',
  sx: externalSx = {},
}: PropsWithChildren<TimelineRowProps>) {
  const itemSx = itemSxMap[clipTimeline]
  const badgeSx = leadingVisual || ['bottom', 'top'].includes(clipTimeline) ? {} : defaultBadgeSx

  return (
    <Timeline.Item sx={{ml: 5, py: '12px', ...itemSx, ...externalSx}}>
      <Timeline.Badge sx={{backgroundColor: 'canvas.default', mr: 0, ...badgeSx}}>
        {leadingVisual ?? <DefaultLeadingVisual />}
      </Timeline.Badge>
      <Timeline.Body sx={{mt: 0}}>{children}</Timeline.Body>
    </Timeline.Item>
  )
}

function TimelineRowHeading({
  title,
  as,
  ...props
}: {title: string; as: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'} & Omit<HeadingProps, 'as'>) {
  return (
    <Heading as={as} sx={{fontWeight: 'normal', fontSize: 1, px: 2, py: 1}} {...props}>
      {title}
    </Heading>
  )
}

export const TimelineRow = Object.assign(TimelineRowComponent, {
  Heading: TimelineRowHeading,
})

try{ DefaultLeadingVisual.displayName ||= 'DefaultLeadingVisual' } catch {}
try{ TimelineRowComponent.displayName ||= 'TimelineRowComponent' } catch {}
try{ TimelineRowHeading.displayName ||= 'TimelineRowHeading' } catch {}
try{ TimelineRow.displayName ||= 'TimelineRow' } catch {}